import { getImageLink as getLegacyImageLink } from '../utils/formatImageLink';
import type { User } from './users';

type ImageUrls = NonNullable<User['imageUrls']>;

export const convertIsoDateToLegacy = (isoDateString: string) => isoDateString.replace(/Z$/, '');

export const legacyPhoneNumbersMapper = {
  fromLegacyToCore: (phoneNumbers: string | null | undefined): User['phoneNumbers'] => {
    const array = phoneNumbers?.replace(/\s/g, '').split(',') ?? [];
    return {
      primary: array[0] || null,
      secondary: array[1] || null,
    };
  },
};

export const legacyImageUrlsMapper = {
  fromLegacyToCore: (imageFilename: string | null): ImageUrls | null => {
    if (!imageFilename) return null;

    return {
      small: getLegacyImageLink({ imageName: imageFilename, size: 'small' }),
      medium: getLegacyImageLink({ imageName: imageFilename, size: 'medium' }),
      large: getLegacyImageLink({ imageName: imageFilename, size: 'large' }),
    };
  },
  fromCoreToLegacy: (imageUrls: ImageUrls | null): string | null =>
    imageUrls?.small.split('/').pop()?.replace('_small', '') || null,
};

const CORE_TO_LEGACY_LANG_CODE: Record<string, string> = { sv: 'se' };

const coreLangCodeToLegacy = (code: string) => CORE_TO_LEGACY_LANG_CODE[code] ?? code;

export function mapLocalizedRecordCoreToLegacy<R extends Record<string, unknown>>(record: R): R;
export function mapLocalizedRecordCoreToLegacy<R extends Record<string, unknown>>(
  record: R | null
): R | null;
export function mapLocalizedRecordCoreToLegacy<R extends Record<string, unknown>>(
  record: R | null | undefined
): R | null | undefined;
export function mapLocalizedRecordCoreToLegacy<R extends Record<string, unknown>>(
  record: R | null | undefined
) {
  if (!record) return record;
  return Object.fromEntries(
    Object.entries(record).flatMap(([key, value]): [string, unknown][] => {
      const legacyKey = coreLangCodeToLegacy(key);
      // A renamed key (sv→se) must not override a legacy key (se) already present.
      if (legacyKey !== key && legacyKey in record) return [];
      return [[legacyKey, value]];
    })
  );
}

export function mapLanguageDiscriminatedListCoreToLegacy<T extends { Language: string }>(
  items: T[]
): T[];
export function mapLanguageDiscriminatedListCoreToLegacy<T extends { Language: string }>(
  items: T[] | null
): T[] | null;
export function mapLanguageDiscriminatedListCoreToLegacy<T extends { Language: string }>(
  items: T[] | null | undefined
): T[] | null | undefined;
export function mapLanguageDiscriminatedListCoreToLegacy<T extends { Language: string }>(
  items: T[] | null | undefined
) {
  if (!items) return items;
  return items.map(item => ({
    ...item,
    Language: coreLangCodeToLegacy(item.Language),
  }));
}
